<template>
    <DCard padding>
        <DRow>
            <DRow
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol
                    sm="3"
                    class="pt-1"
                >
                    <DInput
                        id="tld"
                        v-model="filterParams.tld"
                        :label="__('products.tld_mappings.form.tld')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="pt-1"
                >
                    <DInput
                        id="customer"
                        v-model="filterParams.customer_id"
                        :label="__('products.tld_mappings.form.customer_id')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DCol>
                <DTable
                    class="table-responsive"
                    :sorter="isSortable"
                    :sorter-value="sorterValue"
                    :items="tldMappings.data"
                    :fields="fields"
                    hover
                    @update:sorter-value="updateOrdering"
                >
                    <template #product_id="{item}">
                        <td v-if="item.product_id">
                            <Link
                                v-if="userPermissions.includes('product.view')"
                                :href="productUrl(item.product_id)"
                            >
                                {{ item.product_id }}
                            </Link>
                            <span
                                v-else
                            >{{ item.product_id }}</span>
                        </td>
                        <td v-else />
                    </template>
                    <template #customer_id="{item}">
                        <td v-if="item.customer_id">
                            <Link
                                v-if="userPermissions.includes('customer.view')"
                                :href="customerUrl(item.customer_id)"
                            >
                                {{ item.customer_id }}
                            </Link>
                            <span
                                v-else
                            >{{ item.customer_id }}</span>
                        </td>
                        <td v-else />
                    </template>
                    <template #actions="{item}">
                        <td class="text-right">
                            <DDropdown
                                :id="`actions_dropdown_${item.tld}_${item.product_id}`"
                                :class="'more-actions-button'"
                                :caret="false"
                                icon="dots-horizontal"
                            >
                                <DDropdownItem
                                    icon="pencil"
                                    mini
                                    @click="editItem(item)"
                                >
                                    {{ __('products.tld_mappings.buttons.edit') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    icon="trash-can-outline"
                                    mini
                                    @click="handleDeleteButton(item)"
                                >
                                    {{ __('products.tld_mappings.buttons.delete') }}
                                </DDropdownItem>
                            </DDropdown>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="tldMappings.last_page > 1"
                    :active-page="tldMappings.current_page"
                    :pages="tldMappings.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DModal
            v-model:show="showConfirmationModal"
            :title="__('products.tld_mappings.modal.delete_confirm_modal.title')"
            :message="__('products.tld_mappings.modal.delete_confirm_modal.message')"
            is-confirm
            is-error
            @close="showConfirmationModal = false"
            @confirm="deleteItem"
        />
        <DFabs>
            <DFab
                icon="plus"
                :label="__('products.tld_mappings.buttons.create')"
                @click="router.get(route('products.tld-product-mappings.create'))"
            />
        </DFabs>
    </DCard>
</template>

<script setup>

import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    tldMappings: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const userPermissions = computed(() => usePage().props.auth.permissions)
const showConfirmationModal = ref(false)
const filterParams = {
    tld: props.filters.tld,
    customer_id: props.filters.customer_id,
    order_by: props.filters.order_by,
    order_by_asc: props.filters.order_by_asc ? 1 : 0
}

const fields = [
    { key: 'tld', label: __('products.tld_mappings.form.tld'), sorter: true },
    { key: 'product_id', label: __('products.tld_mappings.form.product_id'), sorter: true },
    { key: 'customer_id', label: __('products.tld_mappings.form.customer_id'), sorter: false },
    { key: 'actions', sorter: false, label: '', _style: { minWidth: '5%' } }
]

const selectedItem = ref(null)
const sorterValue = computed(() => {
    return {
        column: filterParams.order_by,
        asc: !!props.filters.order_by_asc
    }
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const editItem = (item) => router.get(route('products.tld-product-mappings.edit', { id: item.id }))
const deleteItem = () => router.delete(route('products.tld-product-mappings.destroy', { tld_product_mapping: selectedItem.value.id }))

const changePage = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v))

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('products.tld-product-mappings.index', {
        save_filters: true
    }), queryParameters)
}
function resetFilters () {
    router.get(route('products.tld-product-mappings.index', {
        page: props.tldMappings.current_page,
        save_filters: true,
        default_filters: true
    }))
}

function handleDeleteButton (item) {
    showConfirmationModal.value = true
    selectedItem.value = item
}
function updateOrdering (event) {
    filterParams.order_by = event.column
    filterParams.order_by_asc = event.asc ? 1 : 0

    changePage(props.tldMappings.current_page)
}

function customerUrl (customerId) {
    return route('customers.customers.show', {
        customer: customerId
    })
}

function productUrl (productId) {
    return route('products.products.show', {
        product: productId
    })
}

</script>
